import * as React from 'react';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';

const DIALOG_MAX_WIDTH = 'max-w-sm';

interface RemoveUnitsConfirmDialogProps {
  title: string;
  description: string;
  /**
   * Number of the selected units that carry a reservation held by this
   * offer — used only as the show/hide condition. `0` renders the plain
   * description only (offer-unified-table: "Removing units names the
   * reservations it affects").
   */
  reservedCount: number;
  /**
   * Translated notice naming both the selected count and `reservedCount`
   * (e.g. via `t(key, { reserved, total })`). Required whenever
   * `reservedCount > 0` — every user-facing string is the caller's
   * responsibility to route through `t()`.
   */
  reservationNotice?: string;
  /**
   * Of the selected units, how many are not in the current results (a
   * filter hides them) — used only as the show/hide condition, same
   * contract as `reservedCount`.
   */
  hiddenCount?: number;
  /** Translated notice naming `hiddenCount`. Required whenever `hiddenCount > 0`. */
  hiddenNotice?: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RemoveUnitsConfirmDialog({
  title,
  description,
  reservedCount,
  reservationNotice,
  hiddenCount = 0,
  hiddenNotice,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: RemoveUnitsConfirmDialogProps) {
  return (
    <ModalDialog
      header={<span className="text-[14px] font-semibold">{title}</span>}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            data-testid="remove-units-confirm-button"
          >
            {confirmLabel}
          </Button>
        </>
      }
      onClose={onCancel}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="remove-units-confirm-dialog"
    >
      <p className="text-[12px] text-muted-foreground">{description}</p>
      {reservedCount > 0 && reservationNotice ? (
        <p
          className="mt-2 text-xs font-medium text-warning"
          data-testid="remove-units-reservation-notice"
        >
          {reservationNotice}
        </p>
      ) : null}
      {hiddenCount > 0 && hiddenNotice ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="remove-units-hidden-count">
          {hiddenNotice}
        </p>
      ) : null}
    </ModalDialog>
  );
}
